/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { ReviewTab } from "./ReviewTabs";
import { CurrentMedia } from "@rapid/store/slices/mediaSlice";
import { useModel } from "@rapid/contexts/ModelContext";
import {
    useCurrentMedia,
    useSetUserAnnotationsSnapshot,
    useUserAnnotationsSnapshot
} from "@rapid/store";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@rapid/utils/predictions";
import type { AnnotationsForFileAndFrameId, PredictionsForFileAndFrameSegment } from "@rapid/store";
import type { PromptLabels } from "@rapid/store/slices/modelIntentSlice";

type ReviewEditSessionSnapshot = {
    annotationsForFileAndFrameId: AnnotationsForFileAndFrameId;
    predictionsForFileAndFrameSegment: PredictionsForFileAndFrameSegment;
    promptLabels: PromptLabels;
    userAnnotationsSnapshot: AnnotationsForFileAndFrameId;
    textPromptStr: string;
};

interface ReviewContextValue {
    activeTab: ReviewTab;
    setActiveTab: (tab: ReviewTab) => void;
    isEditingModel: boolean;
    startEditingModel: () => void;
    updateModelEdits: () => void;
    cancelModelEdits: () => void;
    hasPromptChanges: boolean;
    markPromptFingerprintClean: () => void;
    // Stores the video's CurrentMedia when switching to annotations view so it
    // can be restored when switching back to predictions.
    savedVideoMedia: CurrentMedia | null;
    setSavedVideoMedia: (media: CurrentMedia | null) => void;
}

const ReviewContext = createContext<ReviewContextValue>({
    activeTab: ReviewTab.DraftModel,
    setActiveTab: () => {},
    isEditingModel: false,
    startEditingModel: () => {},
    updateModelEdits: () => {},
    cancelModelEdits: () => {},
    hasPromptChanges: false,
    markPromptFingerprintClean: () => {},
    savedVideoMedia: null,
    setSavedVideoMedia: () => {}
});

export const useReviewTab = () => useContext(ReviewContext);

function cloneJson<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
}

function clonePromptLabels(promptLabels: PromptLabels): PromptLabels {
    return new Map(Array.from(promptLabels.entries()).map(([key, value]) => [key, { ...value }]));
}

function buildPromptFingerprint(
    promptLabels: PromptLabels,
    annotationsForFileAndFrameId: AnnotationsForFileAndFrameId
) {
    const labels = Array.from(promptLabels.keys()).sort();
    const labelSet = new Set(labels);
    const boxes: string[] = [];

    Object.entries(annotationsForFileAndFrameId).forEach(([fileId, frames]) => {
        Object.entries(frames).forEach(([frameId, annotations]) => {
            annotations.forEach((annotation) => {
                if (annotation.draft) return;
                if (annotation.confidence != null) return;
                if (!labelSet.has(annotation.label)) return;

                boxes.push(
                    [
                        fileId,
                        frameId,
                        annotation.label,
                        Math.round(annotation.x),
                        Math.round(annotation.y),
                        Math.round(annotation.width),
                        Math.round(annotation.height),
                        annotation.empty ? "empty" : "box"
                    ].join(":")
                );
            });
        });
    });

    return JSON.stringify({ labels, boxes: boxes.sort() });
}

export const ReviewContextProvider = ({ children }: { children: React.ReactNode }) => {
    const {
        annotationsForFileAndFrameId,
        predictionsForFileAndFrameSegment,
        promptLabels,
        textPromptStr,
        setAnnotationsForFileAndFrameId,
        setPredictionsForFileAndFrameSegment,
        setPromptLabels,
        setTextPromptStr
    } = useModel();
    const currentMedia = useCurrentMedia();
    const userAnnotationsSnapshot = useUserAnnotationsSnapshot();
    const setUserAnnotationsSnapshot = useSetUserAnnotationsSnapshot();
    const [activeTab, setActiveTab] = useState<ReviewTab>(ReviewTab.DraftModel);
    const [savedVideoMedia, setSavedVideoMedia] = useState<CurrentMedia | null>(null);
    const [isEditingModel, setIsEditingModel] = useState(false);
    const editSessionSnapshotRef = useRef<ReviewEditSessionSnapshot | null>(null);
    const initialPromptFingerprintRef = useRef<string | null>(null);

    const currentPromptFingerprint = useMemo(
        () => buildPromptFingerprint(promptLabels, annotationsForFileAndFrameId),
        [annotationsForFileAndFrameId, promptLabels]
    );

    if (initialPromptFingerprintRef.current === null) {
        initialPromptFingerprintRef.current = currentPromptFingerprint;
    }

    const markPromptFingerprintClean = useCallback(() => {
        initialPromptFingerprintRef.current = buildPromptFingerprint(
            promptLabels,
            annotationsForFileAndFrameId
        );
    }, [annotationsForFileAndFrameId, promptLabels]);

    const hasPromptChanges = initialPromptFingerprintRef.current !== currentPromptFingerprint;

    const startEditingModel = useCallback(() => {
        if (isEditingModel) return;

        editSessionSnapshotRef.current = {
            annotationsForFileAndFrameId: cloneJson(annotationsForFileAndFrameId),
            predictionsForFileAndFrameSegment: cloneJson(predictionsForFileAndFrameSegment),
            promptLabels: clonePromptLabels(promptLabels),
            userAnnotationsSnapshot: cloneJson(userAnnotationsSnapshot),
            textPromptStr
        };

        const confidenceThreshold =
            currentMedia?.file.confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD;

        setPromptLabels(
            new Map(
                Array.from(promptLabels.entries()).map(([key, value]) => [
                    key,
                    { ...value, confidenceThreshold }
                ])
            )
        );
        setIsEditingModel(true);
    }, [
        annotationsForFileAndFrameId,
        currentMedia?.file.confidenceThreshold,
        isEditingModel,
        predictionsForFileAndFrameSegment,
        promptLabels,
        setPromptLabels,
        textPromptStr,
        userAnnotationsSnapshot
    ]);

    const updateModelEdits = useCallback(() => {
        editSessionSnapshotRef.current = null;
        setIsEditingModel(false);
    }, []);

    const cancelModelEdits = useCallback(() => {
        const snapshot = editSessionSnapshotRef.current;
        if (!snapshot) {
            setIsEditingModel(false);
            return;
        }

        setAnnotationsForFileAndFrameId(cloneJson(snapshot.annotationsForFileAndFrameId));
        setPredictionsForFileAndFrameSegment(cloneJson(snapshot.predictionsForFileAndFrameSegment));
        setPromptLabels(clonePromptLabels(snapshot.promptLabels));
        setUserAnnotationsSnapshot(cloneJson(snapshot.userAnnotationsSnapshot));
        setTextPromptStr(snapshot.textPromptStr);
        editSessionSnapshotRef.current = null;
        setIsEditingModel(false);
    }, [
        setAnnotationsForFileAndFrameId,
        setPredictionsForFileAndFrameSegment,
        setPromptLabels,
        setTextPromptStr,
        setUserAnnotationsSnapshot
    ]);

    const value = useMemo(
        () => ({
            activeTab,
            setActiveTab,
            isEditingModel,
            startEditingModel,
            updateModelEdits,
            cancelModelEdits,
            hasPromptChanges,
            markPromptFingerprintClean,
            savedVideoMedia,
            setSavedVideoMedia
        }),
        [
            activeTab,
            cancelModelEdits,
            hasPromptChanges,
            isEditingModel,
            markPromptFingerprintClean,
            savedVideoMedia,
            startEditingModel,
            updateModelEdits
        ]
    );

    return <ReviewContext.Provider value={value}>{children}</ReviewContext.Provider>;
};
